/**
 * SoundHelper.js
 * Centralized audio and speech synthesis helper for:
 * - New Order received alerts (POS, Table scanner, Room scanner)
 * - Item Cancelled alerts (Kitchen Display System)
 */

let audioCtx = null;
let lastPlayedNewOrder = 0;
let lastPlayedCancel = 0;

function getAudioContext() {
  if (typeof window === 'undefined') return null;
  try {
    if (!audioCtx) {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (AudioContextClass) {
        audioCtx = new AudioContextClass();
      }
    }
    if (audioCtx && audioCtx.state === 'suspended') {
      audioCtx.resume().catch(() => {});
    }
    return audioCtx;
  } catch (e) {
    console.warn("AudioContext init error:", e);
    return null;
  }
}

/**
 * Unlocks audio playback for the document on user interaction.
 * Can be called from any click, touch, or sound toggle button.
 */
export function unlockAudio() {
  if (typeof window === 'undefined') return false;
  try {
    const ctx = getAudioContext();
    if (ctx && ctx.state === 'suspended') {
      ctx.resume().catch(() => {});
    }

    if (ctx) {
      const buffer = ctx.createBuffer(1, 1, 22050);
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.connect(ctx.destination);
      source.start(0);
    }

    const primeAudio = new Audio('/cancel_sound.mp3');
    primeAudio.volume = 0.01;
    const p = primeAudio.play();
    if (p !== undefined) {
      p.then(() => {
        primeAudio.pause();
        primeAudio.currentTime = 0;
      }).catch(() => {});
    }

    localStorage.setItem("audioAlertsUnlocked", "true");
    return true;
  } catch (e) {
    console.warn("Error unlocking audio:", e);
    return false;
  }
}

export function isAudioUnlocked() {
  if (typeof window === 'undefined') return false;
  try {
    if (!audioCtx) return localStorage.getItem("audioAlertsUnlocked") === "true";
    return audioCtx.state === 'running';
  } catch (e) {
    return false;
  }
}

/**
 * Play a synthesized multi-tone chime (High-quality notification bell)
 */
export function playSynthNewOrderChime() {
  const ctx = getAudioContext();
  if (!ctx) return;

  if (ctx.state === 'suspended') {
    ctx.resume().catch(() => {});
  }

  const now = ctx.currentTime;
  const notes = [
    { freq: 587.33, start: 0, dur: 0.25 },    // D5
    { freq: 880.00, start: 0.15, dur: 0.35 },  // A5
    { freq: 1174.66, start: 0.30, dur: 0.6 }  // D6
  ];

  notes.forEach(({ freq, start, dur }) => {
    try {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = "sine";
      osc.frequency.setValueAtTime(freq, now + start);

      gain.gain.setValueAtTime(0.001, now + start);
      gain.gain.exponentialRampToValueAtTime(0.4, now + start + 0.04);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + start + dur);

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start(now + start);
      osc.stop(now + start + dur);
    } catch (e) {}
  });
}

/**
 * Play a synthesized warning buzzer (Urgent descending pulses for cancellations)
 */
export function playSynthCancelBuzzer() {
  const ctx = getAudioContext();
  if (!ctx) return;

  if (ctx.state === 'suspended') {
    ctx.resume().catch(() => {});
  }

  const now = ctx.currentTime;
  const pulses = [
    { freq: 850, start: 0, dur: 0.18 },
    { freq: 650, start: 0.22, dur: 0.18 },
    { freq: 480, start: 0.44, dur: 0.30 }
  ];

  pulses.forEach(({ freq, start, dur }) => {
    try {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = "sawtooth";
      osc.frequency.setValueAtTime(freq, now + start);

      gain.gain.setValueAtTime(0.001, now + start);
      gain.gain.exponentialRampToValueAtTime(0.4, now + start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + start + dur);

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start(now + start);
      osc.stop(now + start + dur);
    } catch (e) {}
  });
}

/**
 * Helper to produce clean spoken text for a table/room/source without large database IDs
 */
function getCleanLocationSpeech(options = {}) {
  const tableTitle = options.tableTitle || options.table_title || options.tableName || null;
  const deliveryType = options.deliveryType || null;
  const source = options.source ? options.source.toString().trim() : '';
  const tableId = options.tableId != null ? String(options.tableId).trim() : null;

  // 1. If explicit table title is present
  if (tableTitle && typeof tableTitle === 'string' && tableTitle.trim()) {
    const clean = tableTitle.trim();
    // If it's a numeric ID > 9999, don't read as a table title
    if (!(/^\d{5,}$/.test(clean))) {
      return clean.toLowerCase().startsWith('table') || clean.toLowerCase().startsWith('room')
        ? clean
        : `Table ${clean}`;
    }
  }

  // 2. Check source string
  if (source) {
    // If source matches "Table 16000025", check if the number is large
    const match = source.match(/^Table\s+(\d+)$/i);
    if (match) {
      const num = parseInt(match[1], 10);
      if (num > 9999) {
        // Raw internal ID - suppress the number
        return "a Table";
      }
      return `Table ${match[1]}`;
    }
    // Check if it's already "Room 101" or other clean name
    return source;
  }

  // 3. Room delivery
  if (deliveryType === 'room') {
    return 'Room Service';
  }

  // 4. Raw tableId check
  if (tableId) {
    const num = parseInt(tableId, 10);
    if (num > 9999 || isNaN(num)) {
      return "a Table";
    }
    return `Table ${num}`;
  }

  return null;
}

/**
 * Play sound for New Order Created (Kitchen Display System)
 * Triggered when an order is placed from POS, Flutter Waiter App, or QR Menu
 * @param {Object} options - { tokenNo, tableTitle, tableId, deliveryType, source, speak }
 */
export function playKitchenNewOrderSound(options = {}) {
  const now = Date.now();
  if (now - lastPlayedNewOrder < 800) {
    return; // Debounce rapid triggers
  }
  lastPlayedNewOrder = now;

  console.log("🔊 Playing Kitchen New Order Sound:", options);
  const ctx = getAudioContext();
  if (ctx && ctx.state === 'suspended') {
    ctx.resume().catch(() => {});
  }

  // 1. Play audio file
  try {
    const audio = new Audio('/new_order_sound.mp3');
    audio.volume = 1.0;
    const playPromise = audio.play();
    if (playPromise !== undefined) {
      playPromise.catch(() => {
        playSynthNewOrderChime();
      });
    }
  } catch (e) {
    playSynthNewOrderChime();
  }

  // 2. Play Web Audio API chime as reinforcement
  try {
    playSynthNewOrderChime();
  } catch (e) {}

  // 3. Kitchen Voice Announcement
  if (options.speak !== false && typeof window !== 'undefined' && 'speechSynthesis' in window) {
    try {
      window.speechSynthesis.cancel(); // cancel any stale utterance
      let text = "New order created for kitchen!";
      const loc = getCleanLocationSpeech(options);

      if (loc) {
        text = `New kitchen order for ${loc}!`;
      } else if (options.tokenNo) {
        text = `New order created for kitchen! Token ${options.tokenNo}`;
      } else if (options.deliveryType === 'room') {
        text = "New kitchen order for Room Service!";
      }

      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 1.05;
      utterance.pitch = 1.1;
      window.speechSynthesis.speak(utterance);
    } catch (e) {
      console.warn("Speech synthesis error:", e);
    }
  }
}

/**
 * Play sound for QR Order Request Received (POS & Front of House)
 * Triggered when a guest orders from mobile table scanner or room scanner
 * @param {Object} options - { source, deliveryType, customerName, tableId, speak }
 */
export function playQROrderSound(options = {}) {
  const now = Date.now();
  if (now - lastPlayedNewOrder < 800) {
    return; // Debounce rapid triggers
  }
  lastPlayedNewOrder = now;

  console.log("🛎️ Playing POS QR Order Sound:", options);
  const ctx = getAudioContext();
  if (ctx && ctx.state === 'suspended') {
    ctx.resume().catch(() => {});
  }

  // 1. Play audio file
  try {
    const audio = new Audio('/new_order_sound.mp3');
    audio.volume = 1.0;
    const playPromise = audio.play();
    if (playPromise !== undefined) {
      playPromise.catch(() => {
        playSynthNewOrderChime();
      });
    }
  } catch (e) {
    playSynthNewOrderChime();
  }

  // 2. Web Audio API Chime
  try {
    playSynthNewOrderChime();
  } catch (e) {}

  // 3. POS Voice Announcement
  if (options.speak !== false && typeof window !== 'undefined' && 'speechSynthesis' in window) {
    try {
      window.speechSynthesis.cancel();
      let text = "New order received!";
      const loc = getCleanLocationSpeech(options);

      if (loc) {
        text = `New order received from ${loc}!`;
      } else if (options.deliveryType === 'room') {
        text = "New room order received!";
      }

      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 1.05;
      utterance.pitch = 1.15;
      window.speechSynthesis.speak(utterance);
    } catch (e) {
      console.warn("Speech synthesis error:", e);
    }
  }
}

/**
 * Play sound for New Order Received (General POS & Kitchen fallback)
 * @param {Object} options - { source, deliveryType, customerName, speak }
 */
export function playNewOrderSound(options = {}) {
  if (options.isKitchen) {
    return playKitchenNewOrderSound(options);
  }
  return playQROrderSound(options);
}

/**
 * Play sound for Item Cancelled (Kitchen Display)
 * @param {Object} options - { reason, itemTitle, tableTitle, speak }
 */
export function playItemCancelledSound(options = {}) {
  const now = Date.now();
  if (now - lastPlayedCancel < 1000) {
    return; // Debounce rapid triggers
  }
  lastPlayedCancel = now;

  console.log("Playing item cancelled alert...", options);

  // 1. Play cancel mp3 audio file
  try {
    const audio = new Audio('/cancel_sound.mp3');
    audio.volume = 1.0;
    const playPromise = audio.play();
    if (playPromise !== undefined) {
      playPromise.catch((err) => {
        console.warn("MP3 play failed, using Web Audio synthesizer:", err);
        playSynthCancelBuzzer();
      });
    }
  } catch (e) {
    playSynthCancelBuzzer();
  }

  // 2. Play urgent synthesizer buzzer
  try {
    playSynthCancelBuzzer();
  } catch (e) {
    console.warn("Synth buzzer error:", e);
  }

  // 3. Voice Announcement for kitchen staff
  if (options.speak !== false && typeof window !== 'undefined' && 'speechSynthesis' in window) {
    try {
      let text = "Attention: Order item cancelled!";
      if (options.itemTitle) {
        text = `Attention! ${options.itemTitle} cancelled!`;
      } else if (options.tableTitle) {
        text = `Attention! Item cancelled for ${options.tableTitle}!`;
      }

      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 1.0;
      utterance.pitch = 0.95;
      window.speechSynthesis.speak(utterance);
    } catch (e) {
      console.warn("Speech synthesis error:", e);
    }
  }
}
